// src/services/netSafe.ts
import type { Functions } from 'firebase/functions';
import { httpsCallable } from 'firebase/functions';

export function isProbablyNetworkError(e: any): boolean {
  const msg = String(e?.message || e || '');
  return (
    msg.includes('Failed to fetch') ||
    msg.includes('NetworkError') ||
    msg.includes('Load failed') ||
    msg.includes('ERR_SSL_PROTOCOL_ERROR') ||
    msg.includes('The network connection was lost') ||
    msg.includes('AbortError')
  );
}

function safeAbortable<T>(p: Promise<T>, ms: number): Promise<{ ok: true; value: T } | { ok: false; error: string }> {
  if (!ms || ms <= 0) return p.then((value) => ({ ok: true as const, value })).catch((e) => ({ ok: false as const, error: String(e?.message || e) }));
  let t: any = null;
  const timeout = new Promise<{ ok: false; error: string }>((resolve) => {
    t = setTimeout(() => resolve({ ok: false, error: 'timeout' }), ms);
  });
  return Promise.race([
    p.then((value) => ({ ok: true as const, value })).catch((e) => ({ ok: false as const, error: String(e?.message || e) })),
    timeout,
  ]).finally(() => {
    if (t) clearTimeout(t);
  }) as any;
}

export async function safeFetchJson(
  url: string,
  init: RequestInit = {},
  timeoutMs: number = 12000,
): Promise<{ ok: boolean; status?: number; data?: any; error?: string }> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);

    const p = fetch(url, { ...init, signal: ctrl.signal });
    const r = await p.finally(() => clearTimeout(timer));

    const status = (r as any)?.status;
    const text = await r.text();
    let data: any = null;
    try { data = text ? JSON.parse(text) : null; } catch { data = null; }

    if (!(r as any).ok) return { ok: false, status, error: data?.error || data?.message || text || `http_${status}` };
    return { ok: true, status, data };
  } catch (e: any) {
    const msg = String(e?.message || e);
    return { ok: false, error: isProbablyNetworkError(e) ? 'network_blocked' : msg };
  }
}

export async function safeFetchText(
  url: string,
  init: RequestInit = {},
  timeoutMs: number = 12000,
): Promise<{ ok: boolean; status?: number; text?: string; error?: string }> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);

    const p = fetch(url, { ...init, signal: ctrl.signal });
    const r = await p.finally(() => clearTimeout(timer));

    const status = (r as any)?.status;
    const text = await r.text();

    if (!(r as any).ok) return { ok: false, status, error: text || `http_${status}` };
    return { ok: true, status, text };
  } catch (e: any) {
    const msg = String(e?.message || e);
    return { ok: false, error: isProbablyNetworkError(e) ? 'network_blocked' : msg };
  }
}

/**
 * ✅ Safe httpsCallable wrapper
 * - NEVER throws
 * - Converts network/CORS failures into { ok:false, error:'network_blocked' }
 * - Keeps real function errors in .error (string)
 */
export function safeHttpsCallable<TReq, TRes>(
  functions: Functions,
  name: string,
): (payload: TReq) => Promise<{ data: TRes | { ok: false; error: string } }> {
  const fn = httpsCallable<TReq, any>(functions, name);

  return async (payload: TReq) => {
    try {
      const res = await fn(payload);
      return res as any;
    } catch (e: any) {
      const msg = String(e?.message || e);
      const soft = isProbablyNetworkError(e) ? 'network_blocked' : msg;
      console.warn(`[netSafe] httpsCallable ${name} soft-fail`, soft, e);
      return { data: { ok: false, error: soft } } as any;
    }
  };
}
